import React, { useEffect, useState } from 'react'
import { ButtonCP } from 'submodules/nerit-framework-ui/common/components/button/ButtonCP'
import { ModalCP } from 'submodules/nerit-framework-ui/common/components/modal/ModalCP'
import { TextCP } from 'submodules/nerit-framework-ui/common/components/text/TextCP'
import { useRequest } from 'submodules/nerit-framework-ui/common/request-manager/use-request/UseRequest'
import { RequestUtils } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/RequestUtils'
import { NotificationHelper } from 'submodules/nerit-framework-ui/common/components/notification/inner/NotificationHelper'
import { QAQCRequests } from 'submodules/nerit-aquatec-sdk/services/qaqc/QAQCRequests'
import { QAQCExecutionFilterResponseDTO } from 'submodules/nerit-aquatec-sdk/services/qaqc/dtos/response/QAQCExecutionFilterResponseDTO'
import { QAQCExecutionTypeEnum } from 'submodules/nerit-aquatec-sdk/common/enums/QAQCExecutionTypeEnum'
import { DateUtils } from 'submodules/nerit-framework-utils/utils/date/DateUtils'
import { DateFormatEnum } from 'submodules/nerit-framework-utils/utils/enums/DateFormatEnum'
import { DateRangeSearchRequestDTO } from 'submodules/nerit-framework-utils/sdk-utils/dtos/request/DateRangeSearchRequestDTO'
import styled from 'styled-components'

const EXECUTION_TYPE_LABELS: Record<QAQCExecutionTypeEnum, string> = {
	[QAQCExecutionTypeEnum.IONIC_BALANCE]: 'Balanço iônico',
	[QAQCExecutionTypeEnum.ANOMALY_EVALUATION]: 'Avaliação de anomalias',
}

interface ICPProps {
	executionCode: number
}

/**
 * Abre modal somente leitura com os filtros da execução (get-execution-filter).
 */
export function ButtonViewQAQCExecutionFiltersCP(props: ICPProps): JSX.Element {

	const [visible, setVisible] = useState<boolean>(false)
	const filterRequest = useRequest<QAQCExecutionFilterResponseDTO>()

	useEffect(loadExecutionFilter, [visible, props.executionCode])
	useEffect(onFilterRequestDone, [filterRequest.isAwaiting, visible])

	function loadExecutionFilter(): void {
		if (!visible || !props.executionCode) return
		filterRequest.runRequest(QAQCRequests.getExecutionFilter(props.executionCode))
	}

	function onFilterRequestDone(): void {
		if (!visible || !filterRequest.wasTried || filterRequest.isAwaiting) return
		RequestUtils.isValidRequestReturn(filterRequest, NotificationHelper.DEFAULT_ERROR_GET_MESSAGE)
	}

	function closeModal(): void {
		setVisible(false)
	}

	const dto = filterRequest.responseData

	return (
		<>
			<ButtonCP
				tooltip={'Ver filtros da execução'}
				icon={'filter'}
				onClick={() => setVisible(true)}
				size={'small'}
				type={'ghost'}
			/>
			<ModalCP
				visible={visible}
				title={'Filtros da execução'}
				onCancel={closeModal}
				onOk={closeModal}
				singleButton={true}
				okText={'Fechar'}
				loading={filterRequest.isAwaiting}
				disableOutsideClickClosing={true}
				width={520}
				top={45}
			>
				{!!dto && (
					<FieldsListSCP>
						{buildFilterRows(dto).map((row) => (
							<FieldBlockSCP key={row.label}>
								<TextCP text={row.label} strong={true} marginBottom={4} />
								<ValueLineSCP>{row.value}</ValueLineSCP>
							</FieldBlockSCP>
						))}
					</FieldsListSCP>
				)}
			</ModalCP>
		</>
	)
}

function buildFilterRows(dto: QAQCExecutionFilterResponseDTO): Array<{ label: string; value: string }> {
	return [
		{ label: 'Identificação', value: dto.identification ?? '-' },
		{
			label: 'Tipo de execução',
			value: EXECUTION_TYPE_LABELS[dto.type] ?? String(dto.type),
		},
		{ label: 'Norma', value: dto.regulation?.name ?? '-' },
		{
			label: 'Pontos',
			value: dto.locations?.length ? dto.locations.map((l) => l.name).join(', ') : '-',
		},
		{
			label: 'Parâmetros',
			value: dto.fields?.length ? dto.fields.map((f) => f.name).join(', ') : '-',
		},
		{ label: 'Período', value: formatDateRangeLabel(dto.dateRange) },
	]
}

function formatDateRangeLabel(dateRange?: DateRangeSearchRequestDTO): string {
	if (!dateRange?.beginDate && !dateRange?.endDate) return '-'
	const begin = dateRange.beginDate
		? DateUtils.formatDate(new Date(dateRange.beginDate as unknown as string), DateFormatEnum.BR_WITHOUT_TIME)
		: ''
	const end = dateRange.endDate
		? DateUtils.formatDate(new Date(dateRange.endDate as unknown as string), DateFormatEnum.BR_WITHOUT_TIME)
		: ''
	if (begin && end) return `${begin} a ${end}`
	return begin || end || '-'
}

const FieldsListSCP = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	width: 100%;
`

const FieldBlockSCP = styled.div`
	width: 100%;
`

const ValueLineSCP = styled.div`
	font-size: 0.9rem;
	line-height: 1.45;
	word-break: break-word;
	color: inherit;
`
